import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon Lens Trade-in kampanyası medya planı — 15 Ekim – 15 Kasım 2026.
 *
 * Brief (Merve, 2026-10-14):
 *   "Kampanya tarihi: 15.10.2026 – 15.11.2026. Tasarım: 39.200 TL + KDV tasarım
 *    % 10 ajans hizmet bedeli & stopaj dahil 270 bin TL bütçe. Sadece 1 adet
 *    story, 1 adet Post."
 *
 * 🔑 Sayfa düzeni PV Eylül / Kadrajın Ötesinde ile aynı (Canon için tek doğru
 * referans, Kixx/Hupalupa düzeni KULLANILMAZ). Hedefleme bölümü yok (Eylül'de
 * Efe kaldırttı, kitle iddiasında bulunulmuyor).
 *
 * 🔑 Bütçe: Corals/PV yöntemi — tasarım toplamın İÇİNDEN, kalan üzerinden %10
 * AHK, ardından stopaj. KDV modellenmez. Brief stopaj oranını yazmıyor; son
 * Canon brief'i (PV Eylül) %20 dediği için %20 alındı. %15 çıkarsa tek sabit.
 *
 * 🔑 CPM / CPC Canon'un kendi Meta hesabından (act_3256944827800966), canlı
 * Graph API, 1 Haziran – 13 Ekim 2026, TRAFİK hedefli TEK GÖRSEL reklamlar
 * (Summer Promo, Kadrajın Ötesinde, Back to School story/post'ları),
 * publisher_platform + platform_position kırılımı, yalnız FB + IG:
 * 273.364 ₺ · 8.057.289 gösterim · 119.726 link tıklaması.
 * Facebook Reels (CTR %5,65, Eylül'de %6,21) yanlışlıkla dokunma şüphesiyle
 * plandan çıkarıldı; düz ortalama da onsuz hesaplandı (₺2,45 CPC).
 *
 * ⚠️ 1 Eylül – 13 Ekim aynı kova CPM ₺55,61 / CPC ₺2,90 (tek BTS story'si
 * taşıyor, örneklem ₺68K). Sayfada temkinli senaryo olarak duruyor.
 */
export default function CanonLensTradeIn2026() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');

  // ── Bütçe kırılımı ───────────────────────────────────────────────────────
  const TOTAL = 270000;
  const DESIGN = 39200;
  const AHK_RATE = 0.10;
  const AHK_FEE = Math.round((TOTAL - DESIGN) * AHK_RATE);  // 23.080
  const MEDIA_BRUT = TOTAL - DESIGN - AHK_FEE;              // 207.720
  const STOPAJ_RATE = 0.20;
  const STOPAJ = Math.round(MEDIA_BRUT * STOPAJ_RATE);      // 41.544
  const NET = MEDIA_BRUT - STOPAJ;                          // 166.176

  // ── Süre ──────────────────────────────────────────────────────────────────
  const DAYS = 32; // 15 Ekim → 15 Kasım dahil
  const dailyNet = NET / DAYS;
  const FREQ = 2.5; // tek görsel trafik reklamlarında Tem 2,16 · Eyl 3,48 (reklam düzeyi), 32 gün için orta değer

  // ── Yerleşim bazında bütçe ────────────────────────────────────────────────
  // İki kreatif, beş yerleşim. Story görseli (9:16) Reels + Stories'te, Post
  // görseli (4:5) akışlarda. CPM/CPC o yerleşimin GERÇEKLEŞEN değeri.
  const PLACEMENTS = [
    { name: 'Instagram Reels',   creative: 'Story', size: '1080×1920', placement: 'IG Reels',    tone: 'reels',   cpm: 25.21, cpc: 1.95, share: 0.30, start: '2026-10-15', end: '2026-11-15', role: 'Hacim motoru' },
    { name: 'Instagram Stories', creative: 'Story', size: '1080×1920', placement: 'IG Stories',  tone: 'story',   cpm: 50.18, cpc: 3.83, share: 0.25, start: '2026-10-15', end: '2026-11-15', role: "Story'nin asıl yeri" },
    { name: 'Facebook Stories',  creative: 'Story', size: '1080×1920', placement: 'FB Stories',  tone: 'fbstory', cpm: 54.31, cpc: 2.94, share: 0.08, start: '2026-10-15', end: '2026-11-15', role: 'En yüksek CTR' },
    { name: 'Facebook Feed',     creative: 'Post',  size: '1080×1350', placement: 'FB Feed',     tone: 'fbfeed',  cpm: 26.63, cpc: 1.74, share: 0.25, start: '2026-10-15', end: '2026-11-15', role: 'En ucuz tıklama' },
    { name: 'Instagram Feed',    creative: 'Post',  size: '1080×1350', placement: 'IG Feed',     tone: 'igfeed',  cpm: 42.23, cpc: 2.48, share: 0.12, start: '2026-10-15', end: '2026-11-15', role: 'Profil vitrini' },
  ];

  let alloc = 0;
  const rows = PLACEMENTS.map((p, i) => {
    const isLast = i === PLACEMENTS.length - 1;
    const budget = isLast ? NET - alloc : Math.round(NET * p.share);
    alloc += budget;
    const impressions = Math.round((budget / p.cpm) * 1000);
    const clicks = Math.round(budget / p.cpc);
    const reach = Math.round(impressions / FREQ);
    return { ...p, budget, impressions, clicks, reach };
  });

  const totals = rows.reduce((t, r) => ({
    budget: t.budget + r.budget,
    impressions: t.impressions + r.impressions,
    clicks: t.clicks + r.clicks,
  }), { budget: 0, impressions: 0, clicks: 0 });
  const blendedCpc = totals.budget / totals.clicks;
  const blendedCpm = (totals.budget / totals.impressions) * 1000;
  const estReach = Math.round(totals.impressions / FREQ);
  const ctr = (totals.clicks / totals.impressions) * 100;
  const creativeShare = (c) => rows.filter((r) => r.creative === c).reduce((t, r) => t + r.budget, 0);

  // Ağırlıklandırma yapılmasaydı: tek görsel trafik kovasının düz ortalaması (FB Reels hariç).
  const FLAT_CPC = 2.45;
  const flatClicks = Math.round(NET / FLAT_CPC);
  const gain = ((totals.clicks / flatClicks) - 1) * 100;
  // Temkinli senaryo: 1 Eyl – 13 Eki aynı kovanın blended CPC'si.
  const RECENT_CPC = 2.90;
  const recentClicks = Math.round(NET / RECENT_CPC);

  // ── Takvim ────────────────────────────────────────────────────────────────
  const TL_START = new Date('2026-10-15');
  const TL_END = new Date('2026-11-15');
  const TL_SPAN = (TL_END - TL_START) / 86400000;
  const tlOff = (d) => (new Date(d) - TL_START) / 86400000;
  const tlPct = (d) => (tlOff(d) / TL_SPAN) * 100;
  const fmtDay = (d) => new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  const durDays = (s, e) => Math.round(tlOff(e) - tlOff(s)) + 1;
  const TONES = { reels: '#CC0000', story: '#f59e0b', fbstory: '#ec4899', fbfeed: '#6366f1', igfeed: '#0d9488' };
  const tlMarks = [
    { label: '15 Eki · Lansman', date: '2026-10-15' },
    { label: '1 Kas', date: '2026-11-01' },
    { label: '9 Kas · Son hafta', date: '2026-11-09' },
    { label: '15 Kas · Kapanış', date: '2026-11-15' },
  ];

  // PDF: bölüm bazlı — bir bölüm kalan yere sığmıyorsa ortadan kesilmek yerine
  // yeni sayfaya atılır (BTS planındaki yöntem).
  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const root = slideRef.current;
      const headerEl = root.querySelector('[data-pdf-header]');
      const sectionEls = Array.from(root.querySelectorAll('[data-pdf-section]'));
      const shoot = (el) => html2canvas(el, {
        scale: 2, backgroundColor: '#ffffff', useCORS: true,
        width: el.offsetWidth, height: el.scrollHeight + 12,
        windowWidth: document.documentElement.clientWidth,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const M = 8;
      const GAP = 6;
      const CW = pw - M * 2;
      const BOTTOM = ph - M - 6;

      const headerCanvas = await shoot(headerEl);
      const headerImg = headerCanvas.toDataURL('image/jpeg', 0.95);
      const headerH = (headerCanvas.height * CW) / headerCanvas.width;
      const TOP_Y = M + headerH + GAP;
      const PAGE_H = BOTTOM - TOP_Y;

      let pages = 0;
      let y = 0;
      const startPage = () => {
        if (pages > 0) pdf.addPage();
        pages += 1;
        pdf.addImage(headerImg, 'JPEG', M, M, CW, headerH);
        y = TOP_Y;
      };
      const slice = (canvas, srcY, srcH) => {
        const c = document.createElement('canvas');
        c.width = canvas.width; c.height = srcH;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        return c.toDataURL('image/jpeg', 0.95);
      };

      startPage();
      for (const el of sectionEls) {
        const canvas = await shoot(el);
        const pxPerMm = canvas.width / CW;
        let srcY = 0;
        let remain = canvas.height / pxPerMm;
        while (remain > 0.5) {
          const avail = BOTTOM - y;
          if (remain <= avail) {
            const srcH = Math.round(remain * pxPerMm);
            pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, remain);
            y += remain + GAP;
            remain = 0;
            break;
          }
          if (remain <= PAGE_H) { startPage(); continue; }
          const srcH = Math.round(avail * pxPerMm);
          pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, avail);
          srcY += srcH; remain -= avail;
          startPage();
        }
      }
      for (let i = 1; i <= pages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150);
        pdf.text(`Canon Lens Trade-in — 15 Ekim – 15 Kasım 2026 · Jellybon x Glowytics · ${i}/${pages}`, pw / 2, ph - M + 2, { align: 'center' });
      }
      pdf.save('Canon_Lens_Trade_in_2026_Medya_Plani.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end mb-3">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: '#CC0000' }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div data-pdf-header className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">Lens Trade-in Kampanyası Medya Planı</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Trafik · 15 Ekim – 15 Kasım 2026</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya detayı */}
          <div data-pdf-section className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon <strong>Lens Trade-in</strong> kampanyası. 15 Ekim – 15 Kasım 2026 tarihleri arasında
              ({DAYS} gün) yayında. Amaç: eski lensini yenisiyle değiştirmek isteyen kamera kullanıcısını
              kampanya sayfasına taşımak. Yönlendirme reklam hedefi: <strong>Trafik</strong>,
              birincil KPI <strong>link tıklaması</strong>.
              Brief iki kreatif öngörüyor — <strong>1 adet Story</strong> (1080×1920) ve <strong>1 adet Post</strong> (1080×1350).
              Story görseli Reels ve Stories yerleşimlerinde, Post görseli akışlarda kullanılır; plandaki asıl karar
              <strong> yerleşim ağırlığıdır</strong> (Story {fmtCur(creativeShare('Story'))} · Post {fmtCur(creativeShare('Post'))}).
            </p>
          </div>

          {/* Bütçe kırılımı */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-2">Bütçe Kırılımı</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Toplam Bütçe" value={fmtCur(TOTAL)} sub="müşteri toplam" />
              <Stat label="Tasarım" value={'-' + fmtCur(DESIGN)} sub="1 story + 1 post" tone="indigo" />
              <Stat label="AHK (Ajans %10)" value={'-' + fmtCur(AHK_FEE)} sub={`${fmtCur(TOTAL - DESIGN)} × 0.10`} tone="indigo" />
              <Stat label={`Stopaj (%${STOPAJ_RATE * 100})`} value={'-' + fmtCur(STOPAJ)} sub={`${fmtCur(MEDIA_BRUT)} × ${STOPAJ_RATE.toFixed(2)}`} tone="indigo" />
              <Stat label="NET Medya" value={fmtCur(NET)} sub="Meta'da harcanan" tone="green" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Günlük ortalama net harcama ≈ {fmtCur(dailyNet)} ({DAYS} gün). Tasarım + AHK + Stopaj toplam 270K'ya dahildir.
              Brüt medya {fmtCur(MEDIA_BRUT)}. KDV ayrı bir kalem olarak modellenmemiştir.
              Brief stopaj oranını belirtmediği için son Canon brief'indeki %20 uygulanmıştır; oran %15 ise NET medya
              {' '}{fmtCur(Math.round((TOTAL - DESIGN - AHK_FEE) * 0.85))} olur.
            </p>
          </div>

          {/* Yerleşim dağılımı */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-2">Yerleşim Bazında Bütçe Dağılımı (2 Kreatif · 5 Yerleşim)</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Yerleşim</th>
                    <th className="text-left px-3 py-2 font-semibold">Boyut</th>
                    <th className="text-left px-3 py-2 font-semibold">Rol</th>
                    <th className="text-right px-3 py-2 font-semibold">Bütçe (NET)</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Erişim*</th>
                    <th className="text-right px-3 py-2 font-semibold">Tıklama</th>
                    <th className="text-right px-3 py-2 font-semibold">CPC</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.name} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-800">
                        {r.name}
                        <span className="block text-[9px] font-normal text-gray-400">{r.creative} görseli · {r.placement}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-500">{r.size}</td>
                      <td className="px-3 py-2">
                        <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full border"
                          style={{ color: TONES[r.tone], borderColor: TONES[r.tone] + '40', background: TONES[r.tone] + '12' }}>
                          {r.role}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {fmtCur(r.budget)}
                        <span className="block text-[9px] font-normal text-gray-400">%{Math.round((r.budget / NET) * 100)}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.reach)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.clicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{r.cpc.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{r.cpm.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(estReach)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.clicks)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpc.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpm.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
              *Erişim, ~{FREQ} ortalama frekans varsayımıyla tahminidir. Tahmini CTR ≈ %{ctr.toFixed(2)}.
              CPC ve CPM değerleri her satırda o yerleşimin Canon Meta hesabındaki <strong>gerçekleşen</strong> değeridir
              (1 Haziran – 13 Ekim 2026, trafik hedefli tek görsel story ve post reklamları, toplam {fmtCur(273364)} harcama ·
              {' '}{fmtInt(8057289)} gösterim · {fmtInt(119726)} link tıklaması). Audience Network ve Facebook Reels plandan
              çıkarıldı: Facebook Reels'ın %5,65 tıklama oranı yanlışlıkla dokunma şüphesi taşıyor. Bütçe düz ortalamaya
              (₺{FLAT_CPC.toFixed(2)} CPC) göre dağıtılsaydı {fmtInt(flatClicks)} tıklama beklenirdi; yerleşim ağırlıklandırması bunu
              {' '}<strong>%{gain.toFixed(0)} artırıyor</strong>. Sayılar Meta açık artırma dinamiklerine göre değişebilir.
            </p>
          </div>

          {/* Projeksiyon özeti */}
          <div data-pdf-section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Toplam Tıklama" value={fmtInt(totals.clicks)} sub="link tıklaması (tahmini)" tone="green" />
            <Stat label="Ort. CPC" value={'₺' + blendedCpc.toFixed(2)} sub={`CTR %${ctr.toFixed(2)}`} />
            <Stat label="Toplam Gösterim" value={fmtInt(totals.impressions)} sub={`CPM ₺${blendedCpm.toFixed(2)}`} />
            <Stat label="Tahmini Erişim" value={fmtInt(estReach)} sub={`~${FREQ} frekans`} />
          </div>

          {/* Temkinli senaryo */}
          <div data-pdf-section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-bold text-gray-800">Açıklama gerektiren nokta: son 6 haftanın maliyeti</p>
            <p className="text-xs text-gray-700 mt-1 leading-relaxed">
              Aynı kova (trafik hedefli tek görsel) 1 Eylül – 13 Ekim arasında CPM <strong>₺55,61</strong> · CPC <strong>₺{RECENT_CPC.toFixed(2)}</strong>
              {' '}seviyesinde gerçekleşti; plan tabanı ise ₺33,93 · ₺2,28. Bu dönemi tek bir Back to School story'si taşıyor
              (₺68 bin örneklem) ve kampanya Kasım'daki indirim dönemi öncesine denk geliyor. Maliyetler bu seviyede kalırsa
              aynı bütçe <strong>{fmtInt(recentClicks)} tıklama</strong> getirir (plan: {fmtInt(totals.clicks)}). Plan rakamları
              taban, bu rakam alt sınır olarak okunmalı.
            </p>
          </div>

          {/* İçerik takvimi */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Yayın Takvimi (5 Yerleşim)</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Her yerleşimin bütçe payı (NET üzerinden), yayına giriş tarihi ve yayında kalacağı süre.
              Beş yerleşim 15 Ekim'de birlikte açılır ve kampanya boyunca yayında kalır. Son hafta (9 – 15 Kasım)
              metin "son günler" aciliyetine çevrilir; bütçe performansa göre yerleşimler arasında kaydırılır. Tümü 15 Kasım'da kapanır.
            </p>
            <div className="rounded-xl border border-gray-200 p-4 overflow-hidden">
              {/* Tarih başlıkları */}
              <div className="flex">
                <div className="w-40 md:w-52 shrink-0" />
                <div className="relative flex-1 h-4">
                  {tlMarks.map((m, i) => (
                    <span
                      key={m.label}
                      className="absolute top-0 text-[9px] font-semibold text-gray-400 whitespace-nowrap"
                      style={{
                        left: `${tlPct(m.date)}%`,
                        transform: i === 0 ? 'none' : i === tlMarks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                      }}
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative mt-1.5">
                {/* dikey çizgiler */}
                <div className="absolute inset-0 flex pointer-events-none">
                  <div className="w-40 md:w-52 shrink-0" />
                  <div className="relative flex-1">
                    {tlMarks.map((m) => (
                      <div key={m.label} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: `${tlPct(m.date)}%` }} />
                    ))}
                  </div>
                </div>

                {rows.map((r) => {
                  const left = tlPct(r.start);
                  const width = Math.max(tlPct(r.end) - left, 1.5);
                  const nearEnd = left > 60;
                  return (
                    <div key={r.name} className="flex items-center h-12 relative">
                      <div className="w-40 md:w-52 shrink-0 pr-3">
                        <p className="text-[11px] font-semibold text-gray-800 whitespace-nowrap" style={{ lineHeight: '1.5' }}>{r.name}</p>
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[9px] text-gray-400">{r.creative}</span>
                          <span className="text-[9px] font-bold tabular-nums" style={{ color: TONES[r.tone] }}>
                            {fmtCur(r.budget)} · %{Math.round((r.budget / NET) * 100)}
                          </span>
                        </div>
                      </div>
                      <div className="relative flex-1 h-12">
                        <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${left}%`, width: `${width}%` }}>
                          <div className={`absolute bottom-full mb-1 whitespace-nowrap text-[8.5px] font-medium text-gray-500 ${nearEnd ? 'right-0' : 'left-0'}`}>
                            {fmtDay(r.start)} → {fmtDay(r.end)} · {durDays(r.start, r.end)}g
                          </div>
                          <div className="h-4 rounded-full shadow-sm" style={{ background: TONES[r.tone] }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-3">
                  {rows.map((r) => (
                    <span key={r.name} className="flex items-center gap-1.5 text-[9px] text-gray-500">
                      <span className="w-3 h-3 rounded-full" style={{ background: TONES[r.tone] }} />{r.name}
                    </span>
                  ))}
                </div>
                <span className="text-[9px] text-gray-400">
                  Toplam paid: <strong className="text-gray-600">{fmtCur(NET)}</strong> · {DAYS} gün · 1 story + 1 post
                </span>
              </div>
            </div>
          </div>

          {/* Zamanlama */}
          <div data-pdf-section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-bold text-gray-800">Zamanlama</p>
            <p className="text-xs text-gray-700 mt-1 leading-relaxed">
              Yayın: <strong>15 Ekim – 15 Kasım 2026</strong> ({DAYS} gün), kesintisiz.
              İlk üç hafta trade-in teklifinin kendisini anlatır, 9 Kasım'dan itibaren "son hafta" vurgusu eklenir.
              Bütçe gün bazında dengeli dağıtılır (günlük ≈ {fmtCur(dailyNet)}); yerleşim ağırlıkları kampanya içinde optimize edilir.
            </p>
            <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
              <strong>Ölçümleme ön koşulu:</strong> Canon hesabında link tıklamasının açılış sayfası görüntülemesine dönüşme
              oranı formattan formata çok değişiyor; bu Meta pikselinin bazı açılış sayfalarında tetiklenmediğinin işareti.
              Trade-in sayfası yayına girmeden önce pikselin o sayfada çalıştığı doğrulanmalı.
              <br />
              <strong>Kreatif notu:</strong> 32 gün boyunca iki görselle kişi başı ~{FREQ} tekrara çıkılıyor; üçüncü haftadan sonra
              tıklama oranı düşer. Tasarım bütçesine ek yük getirmeyen önlem: her kreatif için iki ayrı metin ve son hafta için
              aciliyet metni.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">
              {new Date().toLocaleDateString('tr-TR')} · Confidential · Jellybon × Glowytics
            </p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
